import axios from 'axios'
import type { BoxTruckBidLoadsResponse, BoxTruckOfferBotSettings } from '@numeo/types-main-web-app'
import {
    BaseTelegramGroup,
    BOX_TRUCK_NEARBY_DRIVERS_PAGE_SIZE,
    BoxTruckNearbyDriversSort,
    BoxTruckDocument,
    BoxTruckDriverAvailability,
    BoxTruckDriverAvailabilityStatus,
    BoxTruckAssetDocumentDto,
    BoxTruckDriverAssignmentsDto,
    BoxTruckOwnerDocumentDto,
    BoxTruckDriverSourceProfile,
    BoxTruckDriverLastLocation,
    BoxTruckDriverTeamNote,
    BoxTruckNearbyOrigin,
    BoxTruckVerificationStatus,
    CanonicalLoad,
    DriverBoxTruckInfo,
    BoxTruckHrManager,
    IFeature,
    NearbyBoxTruckDriver,
    NearbyBoxTruckDriversResponse,
} from '@numeo/types-core'
import { BoxTruckZipResolveResponse, BoxTruckSenderLines, BoxTruckMailingDto, BoxTruckMailingInput, BoxTruckBroadcastProgress } from '@numeo/types-main-web-app'
import {
    BoxTruckBidder,
    SMS_CONVERSATION_PAGE_MAX,
    SmsConversationPage,
    SmsBulkSendResult,
    SmsConversationSummary,
    SmsThreadMessage,
    BoxTruckLoadAnnotation,
    BoxTruckOperatorBid,
    BoxTruckPricingDecisionInput,
    BoxTruckPricingReportInput,
    BoxTruckPricingQuote,
    BoxTruckPricingRequest,
    LoadBid,
    SmsConversationBot,
    SmsDriverThread,
} from '@numeo/types'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'
import { toBase64Url } from '@/utils/base64url'
import { normalizeLoadCoordinates } from '@/utils/loadCoordinates'

// Backend: main-service mounts the box-truck router at /box-truck-loads (new routing convention, JWT).
// The global axios base is <host>/v1 but these routes have NO /v1 prefix, so each call targets the host
// root via `baseURL: NON_V1_BASE` (the global auth-token interceptor still applies). main-service owns the
// operator allowlist (numeo_config) — box-truck has no general-agent-service dependency. The `/ext` bids
// list (fetchBoxTruckBids) is a separate dat-extension endpoint that keeps its legacy /v1/ext path.
const NON_V1_BASE = getApiBaseUrlWithoutV1()

/** Whether this account is on the Gluon-managed Box Truck Loads operator allowlist (gates the sidebar
 *  section), and whether the Profiles presentation is granted by the ROLE — the half of that grant the
 *  client cannot work out alone, since it turns on an allowlist the browser never sees. Deny by default. (The server also returns a companyOwners flag — unused
 *  since the MC-holder review moved to gluon.) */
export async function fetchBoxTruckLoadsAccess(): Promise<{
    allowed: boolean
    profilesFromRole: boolean
    accountantDefault: boolean
    /** Does this APPLICATION run Box Truck — not "is the caller an operator". Decides whether the
     *  built-in Dispatcher role is read-only on Ongoing Loads; see `usePermissions`. */
    boxTruckCompany: boolean
    pricing: boolean
}> {
    const res = await axios.get<{
        success: boolean
        data: { allowed: boolean; profilesFromRole?: boolean; accountantDefault?: boolean; boxTruckCompany?: boolean; pricing?: boolean }
    }>('/box-truck-loads/operators/access/v1', { baseURL: NON_V1_BASE })
    return {
        allowed: res.data.data.allowed,
        profilesFromRole: res.data.data.profilesFromRole === true,
        accountantDefault: res.data.data.accountantDefault === true,
        boxTruckCompany: res.data.data.boxTruckCompany === true,
        pricing: res.data.data.pricing === true,
    }
}

/**
 * The suggested broker quote and driver offer for one load.
 *
 * Behind its own Gluon tick (`pricing`), and served ONLY for a tenant whose model has been built — the
 * server answers `null` for everyone else, which is the ordinary case and not an error. The caller shows
 * the panel's "no model yet" state rather than a failure.
 */
export async function fetchBoxTruckPricingQuote(request: BoxTruckPricingRequest, signal?: AbortSignal): Promise<BoxTruckPricingQuote | null> {
    const res = await axios.post<{ success: boolean; data: { quote: BoxTruckPricingQuote | null } }>('/box-truck-loads/pricing/quote/v1', request, {
        baseURL: NON_V1_BASE,
        signal,
    })
    return res.data?.data?.quote ?? null
}

/**
 * Record what the dispatcher actually quoted, beside what the panel suggested.
 *
 * Fire-and-forget by design: the bid email has already gone out, so a failure here has nothing the
 * dispatcher could act on, and the server answers 204 whatever happens. Never await it in a send path.
 *
 * Sends the quote REQUEST, not the quote. The server re-prices it and records what it computes.
 */
export async function recordBoxTruckPricingDecision(decision: BoxTruckPricingDecisionInput): Promise<void> {
    await axios.post('/box-truck-loads/pricing/decisions/v1', decision, { baseURL: NON_V1_BASE })
}

/**
 * Report a suggestion as wrong. Goes to the pricing Telegram group; nothing is stored.
 *
 * AWAITED, unlike the decision record next door, and the difference is who is waiting: a decision is a
 * measurement taken after a bid has already gone out, while this is a person watching a dialog. Nothing
 * is persisted, so a failure that the UI swallowed would be the report itself disappearing.
 *
 * `sent: false` is a 200 that did NOT send — the tenant's model was gone by the time the report arrived,
 * so there was no suggestion left to argue with. Throwing is the honest answer: ignoring it told the
 * dispatcher "the pricing team has it" about a message nobody received and nothing recorded.
 */
export async function reportBoxTruckPricingProblem(report: BoxTruckPricingReportInput): Promise<void> {
    const res = await axios.post<{ success: boolean; data?: { sent?: boolean } }>('/box-truck-loads/pricing/report/v1', report, { baseURL: NON_V1_BASE })
    if (res.data?.data?.sent === false) throw new Error('This company has no pricing model right now, so the report could not be sent.')
}

/** A Box Truck bid: the load plus its email-thread signal and who on the team sent it. */
export interface BoxTruckBid {
    load: CanonicalLoad
    emailThreadId: string
    unreadCount: number
    /** Every teammate who bid on this load, newest sender first (usually one). */
    bidders: BoxTruckBidder[]
}

interface BidsResponse {
    success: boolean
    data: { items: BoxTruckBid[]; pagination: { total: number; page: number; limit: number } }
}

/** SYLECTUS_EMAIL loads in this project that already have an email thread (a bid was sent).
 *  `dispatcherId` narrows to the loads that teammate bid on — server-side, so it reaches every page rather
 *  than only the rows already fetched. Served by main-service at GET /v1/ext/box-truck-loads/bids/v1 (JWT,
 *  project-scoped). */
export async function fetchBoxTruckBids(page = 1, limit = 50, dispatcherId?: string): Promise<{ bids: BoxTruckBid[]; total: number }> {
    const res = await axios.get<BidsResponse>('/ext/box-truck-loads/bids/v1', { params: { page, limit, dispatcherId: dispatcherId || undefined } })
    const data = res.data?.data
    return { bids: data?.items ?? [], total: data?.pagination?.total ?? 0 }
}

/** The loadboard's My Biddings tab: loads the CALLER has sent a bid email about, paginated. Whose bids is
 *  decided by the token, which is why the route is "mine" and carries no dispatcher parameter — see
 *  GET /box-truck-loads/operator-bids/mine/v1. Distinct from the Bidding page's list, which names a
 *  teammate. Each row IS a thread, so the thread id and unread count arrive with the load. */
export async function fetchBoxTruckMyBids(page = 1, limit = MY_BIDS_PAGE_SIZE): Promise<{ bids: BoxTruckBid[]; total: number }> {
    const res = await axios.get<BidsResponse>('/box-truck-loads/operator-bids/mine/v1', { baseURL: NON_V1_BASE, params: { page, limit } })
    const data = res.data?.data
    return { bids: data?.items ?? [], total: data?.pagination?.total ?? 0 }
}

/** The Bidding page's dispatcher-filter options: teammates who have actually sent a bid in this project,
 *  with their bid counts. Best-effort — a failure leaves the filter empty rather than breaking the list. */
export async function fetchBoxTruckBidders(): Promise<BoxTruckBidder[]> {
    try {
        const res = await axios.get<{ success: boolean; data: BoxTruckBidder[] }>('/ext/box-truck-loads/bids/dispatchers/v1')
        return res.data?.data ?? []
    } catch {
        return []
    }
}

/** Loadboard "Bid loads" filter, server-side: every load in this project a driver/carrier has bid on (from
 *  load_bids) — not just the loads currently on screen. Optional `startDate`/`endDate` (ISO) narrow by the
 *  load's pickup window, like the loadboard's pickup filter; optional `driverIds` / `equipmentLabels` are the
 *  Driver Bids tab's own filters. Paginated via `limit`/`offset` (infinite scroll)
 *  so a project with 500+ bid loads is fully reachable. Served by GET /box-truck-loads/bid-loads/v1 (JWT,
 *  project-scoped). Returns the page's loads, the grand total of matching bid loads (stable across pages),
 *  and which of the page's loads this desk has not yet emailed a broker about — the ones the server put
 *  first (ADR-057). */
export async function fetchBoxTruckBidLoads(
    startDate?: string,
    endDate?: string,
    offset = 0,
    limit = BID_LOADS_PAGE_SIZE,
    driverIds?: string[],
    equipmentLabels?: string[]
): Promise<BoxTruckBidLoadsResponse> {
    const params: Record<string, string> = { offset: String(offset), limit: String(limit) }
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate
    // The tab's own driver / equipment narrowing, applied SERVER-side (it must reach every bid, not just the
    // rows already scrolled in). The server intersects `drivers` with the caller's claimed fleet.
    if (driverIds?.length) params.drivers = driverIds.join(',')
    if (equipmentLabels?.length) params.equipment = equipmentLabels.join(',')
    const res = await axios.get<{ success: boolean; data: Partial<BoxTruckBidLoadsResponse> }>('/box-truck-loads/bid-loads/v1', {
        params,
        baseURL: NON_V1_BASE,
    })
    return {
        loads: (res.data?.data?.loads ?? []).map((load) => normalizeLoadCoordinates(load)),
        total: res.data?.data?.total ?? 0,
        // Which of these bids nobody here has answered yet. Server-decided, because the server is what
        // ORDERED them — a client that recomputed it could disagree with the order it is rendering.
        awaitingDesk: res.data?.data?.awaitingDesk ?? [],
        // Said only by the store path, whose filters run after the server's LIMIT — there a short page is
        // not the end of the list. Absent, the feed keeps its own short-page rule.
        ...(res.data?.data?.hasMore === undefined ? {} : { hasMore: res.data.data.hasMore }),
        // Where the next page starts, in the units the server pages by — ids, which outnumber the rows that
        // survive its post-LIMIT filters. Absent, the offset is the number of rows held, which is right for
        // every feed that filters inside its query.
        ...(res.data?.data?.nextOffset === undefined ? {} : { nextOffset: res.data.data.nextOffset }),
    }
}

/** Page size for the "Bid loads" infinite scroll — mirrors the live board's PAGE_SIZE. */
export const BID_LOADS_PAGE_SIZE = 150

/** Page size for the loadboard's My Biddings tab. The Bidding PAGE pages 50 at a time because it has a
 *  numbered pager; this board scrolls, and a page shorter than the viewport fires `endReached` on the first
 *  render (see the runaway-pagination fixes), so it asks for a board's worth. */
export const MY_BIDS_PAGE_SIZE = 150

/** Shared main-service overlay client. The default axios export is the configured main-service client
 * (JWT and 401 interceptors are installed in SetupAxios); Box Truck's new-convention routes only need
 * the host-root base URL override. */
// Returns `null` (NOT `{}`) on failure so the caller can DISTINGUISH a failed fetch from a genuinely-empty
// result and keep the existing overlay instead of wiping it. Replacing bids with `{}` on a transient error
// (a blip during one of the frequent auto-refresh ticks) is what made a live driver bid vanish after a while.
const fetchBoxTruckBidOverlay = async <T>(path: string, loads: CanonicalLoad[], label: string): Promise<Record<string, T[]> | null> => {
    if (loads.length === 0) return {}
    try {
        const res = await axios.post<{ success: boolean; data: Record<string, T[]> }>(
            path,
            { loads: loads.filter((load) => !!load.externalId).map(({ externalId, sourceType }) => ({ externalId, sourceType })) },
            { baseURL: NON_V1_BASE }
        )
        return res.data?.data ?? {}
    } catch (err) {
        // Best-effort (a failure just leaves the last-known overlay in place), but log it so a broken
        // endpoint isn't invisible in production.
        console.error(`[box-truck] ${label} failed`, err)
        return null
    }
}

/** Driver/carrier bids placed on the given loads (raw externalIds), keyed by raw externalId. Powers the
 * loadboard driver-bid indicator. `null` on a failed fetch → keep the current bids (don't wipe them). */
export const fetchBoxTruckBidsByExternalId = (loads: CanonicalLoad[]): Promise<Record<string, LoadBid[]> | null> =>
    fetchBoxTruckBidOverlay<LoadBid>('/box-truck-loads/bids/by-external-id/v1', loads, 'fetchBoxTruckBidsByExternalId')

/** Operator bid emails on the given loads, keyed by raw external id. `null` on a failed fetch. */
export const fetchBoxTruckOperatorBidsByExternalId = (loads: CanonicalLoad[]): Promise<Record<string, BoxTruckOperatorBid[]> | null> =>
    fetchBoxTruckBidOverlay<BoxTruckOperatorBid>('/box-truck-loads/operator-bids/by-external-id/v1', loads, 'fetchBoxTruckOperatorBidsByExternalId')

/** The team's star rating + note for the given loads, keyed by raw external id (ONE per load — team-shared).
 *  Powers the loadboard rating column + the drawer's team note. `null` on a failed fetch → keep the current
 *  annotations (don't wipe them). */
export const fetchBoxTruckAnnotationsByExternalId = async (loads: CanonicalLoad[]): Promise<Record<string, BoxTruckLoadAnnotation> | null> => {
    if (loads.length === 0) return {}
    try {
        const res = await axios.post<{ success: boolean; data: Record<string, BoxTruckLoadAnnotation> }>(
            '/box-truck-loads/annotations/by-external-id/v1',
            { loads: loads.filter((load) => !!load.externalId).map(({ externalId, sourceType }) => ({ externalId, sourceType })) },
            { baseURL: NON_V1_BASE }
        )
        return res.data?.data ?? {}
    } catch (err) {
        console.error('[box-truck] fetchBoxTruckAnnotationsByExternalId failed', err)
        return null
    }
}

/** A load's broker email thread state — presence flips the Contact cell's compose icon to the thread icon
 *  (with the unread-reply badge), exactly like Load Hub's contact column. */
export interface BoxTruckEmailThread {
    emailThreadId: string
    unreadCount: number
    /** The load's Mongo id, when the server resolved one. Served by the same response the contact column
     *  reads; declared here so the drawer can open a thread the board already resolved instead of asking
     *  this endpoint again for one load. */
    loadId?: string
}

/** Broker email-thread state for the given loads, keyed by raw external id — the SAME /ext/loads/email-threads
 *  lookup Load Hub's contact column uses (the server resolves each raw id + sourceType to its composite; note
 *  this legacy /v1/ext route uses the default axios base, not NON_V1_BASE). `null` on a failed fetch → keep
 *  the current threads (don't wipe them), matching the sibling overlay fetchers above — Load Hub's own
 *  fetchEmailThreadIds returns {} on failure, which is indistinguishable from "no threads" and would flip a
 *  live thread icon back to compose on a transient blip. */
export const fetchBoxTruckEmailThreads = async (loads: CanonicalLoad[]): Promise<Record<string, BoxTruckEmailThread> | null> => {
    const externalIds = loads.filter((load) => !!load.externalId).map(({ externalId, sourceType }) => ({ externalId, sourceType }))
    if (externalIds.length === 0) return {}
    try {
        const res = await axios.post<{ success: boolean; data: Record<string, BoxTruckEmailThread> }>('/ext/loads/email-threads', { externalIds })
        return res.data?.data ?? {}
    } catch (err) {
        console.error('[box-truck] fetchBoxTruckEmailThreads failed', err)
        return null
    }
}

/** Set the TEAM's star rating + note on a load (the caller sends the full desired state). Returns the saved
 *  annotation. Served by main-service POST /box-truck-loads/annotations/set/v1. */
export const setBoxTruckAnnotation = async (load: CanonicalLoad, rating: number | null, comment: string | null): Promise<BoxTruckLoadAnnotation> => {
    const res = await axios.post<{ success: boolean; data: BoxTruckLoadAnnotation }>(
        '/box-truck-loads/annotations/set/v1',
        { externalId: load.externalId, sourceType: load.sourceType, rating, comment },
        { baseURL: NON_V1_BASE }
    )
    return res.data.data
}

export type { NearbyBoxTruckDriver } from '@numeo/types-core'

interface NearbyBoxTruckDriversPage {
    drivers: NearbyBoxTruckDriver[]
    origin: BoxTruckNearbyOrigin | null
    /** The radius's nearest driver, sent with a `recent` first page only (see `NearbyBoxTruckDriversResponse`). */
    nearest: NearbyBoxTruckDriver | null
    /** The nearest read failed, so `nearest` is unknown rather than none (see `NearbyBoxTruckDriversResponse`). */
    nearestUnavailable: boolean
    hasMore: boolean
    nextCursor: string | null
}

export interface BoxTruckMapDriver {
    driverId: string
    name: string | null
    /** Operator-assigned fleet truck / unit number — shown with the name above the map marker. */
    truckNumber: string | null
    equipment: string | null
    phone: string | null
    city: string | null
    state: string | null
    lat: number
    lng: number
    lastSeenAt: string
    /** false = pending (not yet verified/active) — the map badges these so the operator can tell them apart. */
    verified: boolean
}

export interface BoxTruckMapOverview {
    loadsByState: Array<{ state: string; count: number }>
    drivers: BoxTruckMapDriver[]
}

/** Caller-owned Box Truck drivers (pending and verified) with a shared location within `radiusMi` of the
 *  load origin, in the server's `sort` order (nearest first when omitted). Operator-gated. A `cursor` only
 *  continues the sort it came from. */
export async function fetchNearbyBoxTruckDrivers(
    lat: number | null,
    lng: number | null,
    radiusMi: number,
    includeIds?: string[],
    page?: { cursor?: string; limit?: number },
    namedOrigin?: { city: string; state: string },
    sort?: BoxTruckNearbyDriversSort
): Promise<NearbyBoxTruckDriversPage> {
    interface LegacyPagination {
        hasMore: boolean
        nextCursor: string | null
    }
    type LegacyResponseData = NearbyBoxTruckDriver[] | { items: NearbyBoxTruckDriver[]; pagination: LegacyPagination }
    type NearbyResponse =
        | NearbyBoxTruckDriversResponse
        | { success: boolean; data: LegacyResponseData; pagination?: LegacyPagination; origin?: BoxTruckNearbyOrigin }
    const res = await axios.get<NearbyResponse>('/box-truck-loads/matching/nearby-drivers/v1', {
        // `include`: driver ids that must appear regardless of radius/location — the load's bidders.
        params: {
            ...(lat != null && lng != null ? { lat, lng } : namedOrigin),
            radiusMi,
            ...(sort ? { sort } : {}),
            ...(includeIds?.length ? { include: includeIds.join(',') } : {}),
            limit: page?.limit ?? BOX_TRUCK_NEARBY_DRIVERS_PAGE_SIZE,
            ...(page?.cursor ? { cursor: page.cursor } : {}),
        },
        baseURL: NON_V1_BASE,
    })
    const responseData = res.data?.data
    const nestedData = responseData && !Array.isArray(responseData) ? responseData : undefined
    const pagination = res.data?.pagination ?? nestedData?.pagination
    return {
        drivers: Array.isArray(responseData) ? responseData : (nestedData?.items ?? []),
        origin: res.data.origin ?? null,
        nearest: ('nearest' in res.data ? res.data.nearest : null) ?? null,
        nearestUnavailable: 'nearestUnavailable' in res.data && res.data.nearestUnavailable === true,
        hasMore: pagination?.hasMore === true,
        nextCursor: pagination?.nextCursor ?? null,
    }
}

/** Public "open in app, else store" landing URL for a box-truck load — this is what goes in the driver
 *  SMS. Tapping it opens the load in the Numeo app if installed, else redirects to the App Store / Play
 *  Store (main-service `GET /v1/mobile-app/open/load/:id/app`). The id is the composite `SOURCE|rawId`
 *  the app + loads table key on, base64url-encoded so the SMS URL stays clean (hides the `SOURCE|` prefix)
 *  — same encoding as the in-app Share Load link; loads-ingest `by-external-id` decodes it on the way back. */
export function buildBoxTruckLoadAppLinkUrl(load: Pick<CanonicalLoad, 'externalId' | 'sourceType'>): string {
    const rawId = load.externalId ?? ''
    const composite = rawId.includes('|') ? rawId : `${load.sourceType ?? 'SYLECTUS_EMAIL'}|${rawId}`
    return `${NON_V1_BASE}/v1/mobile-app/open/load/${encodeURIComponent(toBase64Url(composite))}/app`
}

/**
 * Whether the load bot answers this company's drivers at all (ADR-057), and how many have texted STOP.
 *
 * The tenant-wide switch, which is not the same as the one in a conversation: that one is a dispatcher
 * taking over one thread, this one is the company's. Served by the team settings endpoints.
 */
/** `canChange` is the server's answer about THIS operator: the switch is an admin's (ADR-057 §14). */
export async function fetchBoxTruckOfferBot(): Promise<BoxTruckOfferBotSettings> {
    const res = await axios.get<{ success: boolean; data: Partial<BoxTruckOfferBotSettings> }>('/box-truck-loads/settings/offer-bot/v1', {
        baseURL: NON_V1_BASE,
    })
    const answer = res.data?.data ?? {}
    // A field absent means an older server. `reached` reads as NOT reached, which is the fail-closed side
    // and the same side the bot's own gate takes.
    return { on: answer.on === true, optedOutCount: answer.optedOutCount ?? 0, canChange: answer.canChange === true, reached: answer.reached === true }
}

/** Turn it off, or back on, for the whole company. Answers the state it is now in. */
export async function setBoxTruckOfferBot(on: boolean): Promise<{ on: boolean }> {
    const res = await axios.put<{ success: boolean; data: { on: boolean } }>('/box-truck-loads/settings/offer-bot/v1', { on }, { baseURL: NON_V1_BASE })
    return res.data.data
}

/** The team's saved My Drivers recruiting SMS template, or null when the team hasn't customized it (the
 *  caller then seeds the bundled default). Served by GET /box-truck-loads/settings/recruit-sms/v1. */
export async function fetchBoxTruckRecruitSmsTemplate(): Promise<string | null> {
    const res = await axios.get<{ success: boolean; data: { template: string | null } }>('/box-truck-loads/settings/recruit-sms/v1', { baseURL: NON_V1_BASE })
    return res.data.data.template
}

/** A driver's Box Truck bidding-verification request, as shown in the operator queue. */
export interface BoxTruckVerification {
    userId: string
    email: string
    name: string
    boxTruckInfo: DriverBoxTruckInfo
    /**
     * Does a Numeo account stand behind this driver?
     *
     * False for one an HR added from Profiles: the fleet record exists, but nobody has signed in as them, so
     * there is no account to hold a location, a bid or an App Access grant. The row is listed and editable;
     * what the driver themselves would have to provide simply is not there yet.
     */
    appAccountLinked?: boolean
    /** Latest last-known location, however it was captured (`lastLocation.source`). */
    lastLocation?: BoxTruckDriverLastLocation
    /** Driver profile city/state; this is separate from the raw GPS fix. */
    profileLocation?: { city?: string; state?: string }
    /** 'added' when this driver is claimed by the calling operator (instant, no migration). Absent = not
     *  claimed by this operator, so "Add My Drivers" is actionable. */
    claimStatus?: 'added'
    /** The FLEET DRIVER record id behind the caller's claim — what driver-record surfaces (the Telegram
     *  /connect command) key on, distinct from `userId` (the standalone account). Caller-owned rows only. */
    claimDriverId?: string
    /** The claim record's linked Telegram group — powers the Messaging Services panel. */
    telegramGroup?: BaseTelegramGroup
    /** The operator-controlled App Access grants for this driver (My Drivers list only). */
    appAccess?: IFeature[]
    /** Who claimed this driver into My Drivers — the driver's HR manager. Caller-owned rows only; forward-only
     *  (absent for drivers claimed before this shipped). */
    hrManager?: BoxTruckHrManager
    /** Imported Atrek lifecycle, owner relation and driver preferences. Management response only. */
    sourceProfile?: BoxTruckDriverSourceProfile
    /** This team's private availability + note (from box_truck_driver_annotations); null when unset. */
    availability?: BoxTruckDriverAvailability | null
    teamNote?: BoxTruckDriverTeamNote | null
}

interface VerificationsResponse {
    success: boolean
    data: { items: BoxTruckVerification[]; pagination: { total: number; page: number; limit: number } }
}

/** Which review queue to fetch — MC-holders (Company Owners) vs the classic driver queue. */
export type BoxTruckVerificationAudience = 'company_owners' | 'drivers'

/** Operator queue of driver Box Truck verification requests (cross-tenant). Optional status + audience filter.
 *  Served by main-service GET /box-truck-loads/verifications/list/v1 (operator-gated). */
export async function fetchBoxTruckVerifications(
    page = 1,
    limit = 50,
    status?: BoxTruckVerificationStatus,
    search?: string,
    audience?: BoxTruckVerificationAudience,
    equipment?: string
): Promise<{ items: BoxTruckVerification[]; total: number }> {
    const params: Record<string, string | number> = { page, limit }
    if (status) params.status = status
    if (search?.trim()) params.search = search.trim()
    if (audience) params.audience = audience
    if (equipment) params.equipment = equipment
    const res = await axios.get<VerificationsResponse>('/box-truck-loads/verifications/list/v1', { params, baseURL: NON_V1_BASE })
    const data = res.data?.data
    return { items: data?.items ?? [], total: data?.pagination?.total ?? 0 }
}

/** Flip a driver's verification status. 'active' enables their bidding, 'inactive' disables it.
 *  Returns the updated boxTruckInfo so the caller can map-replace the row. */
export async function setBoxTruckVerificationStatus(userId: string, status: BoxTruckVerificationStatus): Promise<DriverBoxTruckInfo> {
    const res = await axios.patch<{ success: boolean; data: DriverBoxTruckInfo }>(
        `/box-truck-loads/verifications/${userId}/status/v1`,
        { status },
        { baseURL: NON_V1_BASE }
    )
    return res.data.data
}

/** INSTANT claim a box-truck driver into the operator's roster (no migration). They appear in My Drivers.
 *  `leadSource` (optional) records how the driver was sourced, picked in the Add-driver modal. */
/** Claim a driver into My Drivers. Answers with the FLEET driver id — a different id from the account one
 *  passed in, and the one anything done for this driver next (a vehicle, their profile) is keyed on. */
export async function claimBoxTruckDriver(userId: string, leadSource?: string): Promise<{ driverId?: string }> {
    const res = await axios.post<{ success: boolean; data: { driverId?: string } }>(
        `/box-truck-loads/verifications/${userId}/claim/v1`,
        leadSource ? { leadSource } : {},
        { baseURL: NON_V1_BASE }
    )
    return { driverId: res.data?.data?.driverId }
}

/** The team's CUSTOM lead sources (the Numeo/Facebook/Indeed defaults are client-side). GET settings/lead-sources. */
export async function fetchBoxTruckLeadSources(): Promise<string[]> {
    const res = await axios.get<{ success: boolean; data: { leadSources: string[] } }>('/box-truck-loads/settings/lead-sources/v1', { baseURL: NON_V1_BASE })
    return res.data?.data?.leadSources ?? []
}

/** Add a custom lead source to the team's list (deduped server-side); returns the updated list. */
export async function addBoxTruckLeadSource(leadSource: string): Promise<string[]> {
    const res = await axios.post<{ success: boolean; data: { leadSources: string[] } }>(
        '/box-truck-loads/settings/lead-sources/v1',
        { leadSource },
        { baseURL: NON_V1_BASE }
    )
    return res.data?.data?.leadSources ?? []
}

/** The operator's own claimed box-truck drivers. Management receives the complete registration and App
 *  Access grants; other operator users receive operational fields only. */
export async function fetchMyBoxTruckDrivers(
    page = 1,
    limit = 50,
    search?: string,
    filters?: { equipment?: string; manager?: string; expiry?: string; source?: string; group?: string; driverId?: string }
): Promise<{ items: BoxTruckVerification[]; total: number }> {
    const params: Record<string, string | number> = { page, limit }
    if (search?.trim()) params.search = search.trim()
    if (filters?.equipment) params.equipment = filters.equipment
    if (filters?.manager) params.manager = filters.manager
    if (filters?.expiry) params.expiry = filters.expiry
    if (filters?.source) params.source = filters.source
    if (filters?.group) params.group = filters.group
    if (filters?.driverId) params.driverId = filters.driverId
    const res = await axios.get<VerificationsResponse>('/box-truck-loads/my-drivers/v1', { params, baseURL: NON_V1_BASE })
    const data = res.data?.data
    return { items: data?.items ?? [], total: data?.pagination?.total ?? 0 }
}

/** Distinct HR managers across the operator's box-truck fleet — options for the My Drivers "Manager" filter.
 *  Served by GET /box-truck-loads/my-drivers/managers/v1. */
export async function fetchMyBoxTruckDriverHrManagers(): Promise<{ userId: string; name: string }[]> {
    const res = await axios.get<{ success: boolean; data: { items: { userId: string; name: string }[] } }>('/box-truck-loads/my-drivers/managers/v1', {
        baseURL: NON_V1_BASE,
    })
    return res.data?.data?.items ?? []
}

/** Set a claimed driver's dispatch availability (Available / Unavailable / Booked). Any context lives in the
 *  team note. Returns the stored availability. PATCH /my-drivers/:userId/availability/v1. */
export async function setBoxTruckDriverAvailability(userId: string, status: BoxTruckDriverAvailabilityStatus): Promise<BoxTruckDriverAvailability> {
    const res = await axios.patch<{ success: boolean; data: { availability: BoxTruckDriverAvailability } }>(
        `/box-truck-loads/my-drivers/${userId}/availability/v1`,
        { status },
        { baseURL: NON_V1_BASE }
    )
    return res.data.data.availability
}

/** Set (or clear, with empty text) a claimed driver's team note. Returns the stored note, or null when cleared.
 *  PATCH /my-drivers/:userId/note/v1. */
export async function setBoxTruckDriverNote(userId: string, text: string): Promise<BoxTruckDriverTeamNote | null> {
    const res = await axios.patch<{ success: boolean; data: { teamNote: BoxTruckDriverTeamNote | null } }>(
        `/box-truck-loads/my-drivers/${userId}/note/v1`,
        { text },
        { baseURL: NON_V1_BASE }
    )
    return res.data.data.teamNote
}

/** Operator-corrected driver location. The editor geocodes the typed city/ZIP to a point first; the server
 *  writes BOTH the last-known-fix store (what Nearby Drivers / the map / list cells read) and the driver's
 *  profile locality, then returns the stored fix. PATCH /my-drivers/:userId/location/v1. */
interface DriverLocationUpdate {
    lat: number
    lng: number
    city?: string
    state?: string
    postalCode?: string
}
export type DriverLastLocationDto = BoxTruckDriverLastLocation
export async function updateBoxTruckDriverLocation(userId: string, input: DriverLocationUpdate): Promise<DriverLastLocationDto> {
    const res = await axios.patch<{ success: boolean; data: { lastLocation: DriverLastLocationDto } }>(
        `/box-truck-loads/my-drivers/${userId}/location/v1`,
        input,
        { baseURL: NON_V1_BASE }
    )
    return res.data.data.lastLocation
}

/** Fetch one of a claimed driver's documents as a blob object URL for in-place preview (image/video/PDF).
 *  The bytes are proxied through main-service (audited, JWT-authed via the axios interceptor) rather than a
 *  signed URL, so it works in every environment. Caller must URL.revokeObjectURL(...) when done.
 *  GET /my-drivers/:userId/documents/file/v1?gcsPath=. */
export async function fetchBoxTruckDriverDocumentBlobUrl(userId: string, gcsPath: string): Promise<string> {
    const res = await axios.get<Blob>(`/box-truck-loads/my-drivers/${userId}/documents/file/v1`, {
        baseURL: NON_V1_BASE,
        params: { gcsPath },
        responseType: 'blob',
    })
    return URL.createObjectURL(res.data)
}

/** A QUEUED driver's self-submitted documents, for review from the verification queues (operator-gated, NOT
 *  claim-gated). GET /box-truck-loads/verifications/:userId/documents/v1. */
export async function fetchBoxTruckVerificationDocuments(userId: string): Promise<BoxTruckDocument[]> {
    const res = await axios.get<{ success: boolean; data: { documents: BoxTruckDocument[] } }>(`/box-truck-loads/verifications/${userId}/documents/v1`, {
        baseURL: NON_V1_BASE,
    })
    return res.data?.data?.documents ?? []
}

/**
 * The records this driver is ATTACHED to — the vehicle seated on their claim and the owner behind it, with
 * the documents those two hold.
 *
 * Its own call, made when a driver is opened, for the same reason the documents call is: the list is a page
 * of fifty, and an owner plus a vehicle per row would be fifty times a cost one driver at a time pays once.
 * GET /box-truck-loads/my-drivers/:userId/assignments/v1.
 */
export async function fetchBoxTruckDriverAssignments(userId: string): Promise<BoxTruckDriverAssignmentsDto> {
    const res = await axios.get<{ success: boolean; data: BoxTruckDriverAssignmentsDto }>(`/box-truck-loads/my-drivers/${userId}/assignments/v1`, {
        baseURL: NON_V1_BASE,
    })
    return res.data?.data ?? { vehicle: null, owner: null }
}

/**
 * The owner's / the assigned vehicle's document LIST, asked for when the operator says so.
 *
 * Not part of the assignments response: most opens of a driver are somebody checking a phone number or a
 * VIN, and reading two document arrays on every one of them to serve the few that go on to open a file is a
 * cost paid for nothing most of the time.
 *
 * Until a vehicle holds them, the vehicle list is the vehicle documents the DRIVER handed in — for a driver
 * the caller has not added (Driver Verifications), only the driver's own uploads.
 */
const fetchAssignmentDocuments = async <T>(userId: string, kind: 'owner-documents' | 'vehicle-documents'): Promise<T[]> => {
    const res = await axios.get<{ success: boolean; data: { documents: T[] } }>(`/box-truck-loads/my-drivers/${userId}/assignments/${kind}/v1`, {
        baseURL: NON_V1_BASE,
    })
    return res.data?.data?.documents ?? []
}

export const fetchAssignedOwnerDocuments = (userId: string): Promise<BoxTruckOwnerDocumentDto[]> =>
    fetchAssignmentDocuments<BoxTruckOwnerDocumentDto>(userId, 'owner-documents')

export const fetchAssignedVehicleDocuments = (userId: string): Promise<BoxTruckAssetDocumentDto[]> =>
    fetchAssignmentDocuments<BoxTruckAssetDocumentDto>(userId, 'vehicle-documents')

/**
 * The owner's / the vehicle's file, through the MY DRIVERS routes rather than the Profiles ones.
 *
 * The Profiles routes sit behind `boxTruckProfilesGuard`, and management access does not imply that grant —
 * a caller holding the `BOX_TRUCK_DRIVERS` permission without the per-member checkbox would be shown the
 * chips and refused every one of them. The contractor and asset ids come from the caller's claim on the
 * driver, so neither is sent from here; without a claim, only the driver's own vehicle uploads are served.
 *
 * Caller must `URL.revokeObjectURL(...)` when done.
 */
const fetchAssignmentDocumentBlobUrl = async (userId: string, kind: 'owner-documents' | 'vehicle-documents', documentId: string): Promise<string> => {
    const res = await axios.get<Blob>(`/box-truck-loads/my-drivers/${userId}/assignments/${kind}/${documentId}/file/v1`, {
        baseURL: NON_V1_BASE,
        responseType: 'blob',
    })
    return URL.createObjectURL(res.data)
}

export const fetchAssignedOwnerDocumentBlobUrl = (userId: string, documentId: string): Promise<string> =>
    fetchAssignmentDocumentBlobUrl(userId, 'owner-documents', documentId)

export const fetchAssignedVehicleDocumentBlobUrl = (userId: string, documentId: string): Promise<string> =>
    fetchAssignmentDocumentBlobUrl(userId, 'vehicle-documents', documentId)

/** Stream one of a queued driver's self-submitted documents as a blob object URL (audited). Caller must
 *  URL.revokeObjectURL(...) when done. GET /box-truck-loads/verifications/:userId/documents/file/v1?gcsPath=. */
export async function fetchBoxTruckVerificationDocumentBlobUrl(userId: string, gcsPath: string): Promise<string> {
    const res = await axios.get<Blob>(`/box-truck-loads/verifications/${userId}/documents/file/v1`, {
        baseURL: NON_V1_BASE,
        params: { gcsPath },
        responseType: 'blob',
    })
    return URL.createObjectURL(res.data)
}

// ─── Mailings (scheduled recurring Telegram / SMS broadcasts) ───────────────────────────────────────────

export async function fetchBoxTruckMailings(): Promise<BoxTruckMailingDto[]> {
    const res = await axios.get<{ success: boolean; data: { items: BoxTruckMailingDto[] } }>('/box-truck-loads/mailings/v1', { baseURL: NON_V1_BASE })
    return res.data.data.items
}

export async function createBoxTruckMailingApi(input: BoxTruckMailingInput): Promise<BoxTruckMailingDto> {
    const res = await axios.post<{ success: boolean; data: BoxTruckMailingDto }>('/box-truck-loads/mailings/v1', input, { baseURL: NON_V1_BASE })
    return res.data.data
}

export async function updateBoxTruckMailingApi(id: string, input: Partial<BoxTruckMailingInput>): Promise<BoxTruckMailingDto> {
    const res = await axios.patch<{ success: boolean; data: BoxTruckMailingDto }>(`/box-truck-loads/mailings/${id}/v1`, input, { baseURL: NON_V1_BASE })
    return res.data.data
}

export async function deleteBoxTruckMailingApi(id: string): Promise<void> {
    await axios.delete(`/box-truck-loads/mailings/${id}/v1`, { baseURL: NON_V1_BASE })
}

/** The Quo lines this operator may send from — their own groups' lines, plus the company's. Shared by
 *  the mailing form and the SMS composer, which ask the same question of the same endpoint. */
export async function fetchBoxTruckSenderLines(check?: string | null): Promise<BoxTruckSenderLines> {
    const res = await axios.get<{ success: boolean; data: BoxTruckSenderLines }>('/box-truck-loads/sms/sender-lines/v1', {
        baseURL: NON_V1_BASE,
        // `check` asks what became of a line we already hold the id for — see `checked` on the answer.
        params: check ? { check } : undefined,
    })
    return res.data.data
}

// ─── SMS: conversations with drivers, over the company's Quo line (ADR-043) ─────────────────────────────

/** One page of conversations. `cursor` continues from a previous page; the answer carries the next one. */
async function fetchSmsConversationPage(cursor?: string | null, limit?: number, fresh?: boolean): Promise<SmsConversationPage> {
    const res = await axios.get<{ success: boolean; data: SmsConversationPage }>('/box-truck-loads/sms/conversations/v1', {
        baseURL: NON_V1_BASE,
        params: { ...(cursor ? { cursor } : {}), ...(limit ? { limit } : {}), ...(fresh ? { fresh: 1 } : {}) },
    })
    return res.data.data
}

/**
 * Every conversation, fetched a page at a time.
 *
 * The list used to arrive whole and stop at two hundred, so a tenant with more could not reach the
 * rest — not by scrolling and not through the search box, which filters what is already loaded. The
 * walk keeps the whole list in hand (so search still searches all of it) while the first page paints
 * immediately: `onPage` is called with the rows so far after each one.
 *
 * `seen` de-duplicates across pages. The cursor is keyset, so a conversation that receives a message
 * mid-walk jumps to the top and can be handed out twice; without this it would render twice too.
 */
export async function fetchAllSmsConversations(
    onPage?: (soFar: SmsConversationSummary[]) => void,
    /**
     * This reload FOLLOWS a send, so the server must not answer it from a list written before.
     *
     * The conversation cache is dropped by the pod that writes, and only inbound messages reach the
     * others over the socket — so a bulk send on one pod and a reload served by another showed a list
     * with the new threads missing, and the client had no local row to merge in their place.
     */
    afterSend = false
): Promise<SmsConversationSummary[]> {
    const all: SmsConversationSummary[] = []
    const seen = new Set<string>()
    let cursor: string | null = null
    // THREE requests is the real shape — 50, then 500, then the rest of a 1000-row ceiling. The bound
    // is slack above that, not a budget: a cursor that somehow stopped advancing must end as a short
    // list rather than as an endless loop of requests.
    for (let page = 0; page < 5; page++) {
        // The FIRST page is small because it is what the operator waits for; the rest come in the
        // server's largest page, so a full inbox is two or three requests instead of twenty. Each
        // request re-runs the group scope over the tenant's whole list, so the count is the cost.
        //
        // AFTER A SEND it is one big fresh page instead, and that is not an optimisation. Only the
        // bypassing request is guaranteed to see the write; the pages behind it can land on any pod
        // and walk ITS list, which may not hold the new threads at all. The newest rows sort first and
        // a send reaches at most `SMS_MAX_IMMEDIATE_RECIPIENTS` drivers, so one page of
        // `SMS_CONVERSATION_PAGE_MAX` covers every thread the send could have created.
        const first = page === 0
        const next: SmsConversationPage = await fetchSmsConversationPage(
            cursor,
            first && !afterSend ? undefined : SMS_CONVERSATION_PAGE_MAX,
            afterSend && first
        )
        for (const row of next.items) {
            const key = `${row.line}|${row.counterpart}`
            if (seen.has(key)) continue
            seen.add(key)
            all.push(row)
        }
        onPage?.([...all])
        if (!next.nextCursor) break
        cursor = next.nextCursor
    }
    return all
}

/** One conversation: a driver's number AND the company line it is on, which together are the thread. */
export async function fetchSmsThread(counterpart: string, line: string): Promise<SmsThreadMessage[]> {
    const res = await axios.get<{ success: boolean; data: { messages: SmsThreadMessage[] } }>('/box-truck-loads/sms/thread/v1', {
        baseURL: NON_V1_BASE,
        params: { counterpart, line },
    })
    return res.data.data.messages
}

/**
 * One driver's conversation, asked for by DRIVER — what the load drawer has to work with.
 *
 * The number and the line are the server's to decide: the number is the one the tenant would text,
 * and the line is the caller's own conversation with them where there is one, else the most recent.
 * A `line` of null means nobody has spoken to them yet, and the composer then sends without naming a
 * thread. `lines` carries the others, for the switcher.
 */
export async function fetchDriverSmsThread(driverAccountId: string, line?: string | null): Promise<SmsDriverThread> {
    const res = await axios.get<{ success: boolean; data: SmsDriverThread }>('/box-truck-loads/sms/driver-thread/v1', {
        baseURL: NON_V1_BASE,
        // `line` only when the operator picked one of the other conversations with this driver;
        // without it the server opens theirs, falling back to the most recent.
        params: line ? { driverAccountId, line } : { driverAccountId },
    })
    return res.data.data
}

/** Whether the load bot is answering ONE driver's conversation, and whose decision it is when it is not. */
export async function fetchConversationBot(driverAccountId: string): Promise<SmsConversationBot> {
    const res = await axios.get<{ success: boolean; data: SmsConversationBot }>('/box-truck-loads/sms/bot/v1', {
        baseURL: NON_V1_BASE,
        params: { driverAccountId },
    })
    return res.data.data
}

/**
 * Turn the load bot on or off for ONE driver's conversation (ADR-057).
 *
 * The answer is the state the bot is actually in, not an echo: a driver who texted STOP stays off however
 * this is flipped, and the thread says so rather than showing a switch that pretends otherwise.
 */
export async function setConversationBot(driverAccountId: string, on: boolean): Promise<SmsConversationBot> {
    const res = await axios.post<{ success: boolean; data: SmsConversationBot }>(
        '/box-truck-loads/sms/bot/v1',
        { driverAccountId, on },
        { baseURL: NON_V1_BASE }
    )
    return res.data.data
}

export async function markSmsThreadRead(counterpart: string, line: string): Promise<void> {
    await axios.post('/box-truck-loads/sms/thread/read/v1', { counterpart, line }, { baseURL: NON_V1_BASE })
}

/**
 * One driver or many — the composer sends one id, the bulk picker sends a list.
 *
 * A reply passes `thread`: the conversation it answers. Its SENDING line is not passed — the server
 * reads it off the stored thread, so an answer cannot leave on a different number than the one the
 * driver was written from, whatever the page believes. `senderPhoneNumberId` is for a NEW message
 * only, and the server ignores it on a reply for the same reason.
 */
export async function sendSmsToDrivers(
    driverIds: string[],
    message: string,
    thread?: { counterpart: string; line: string },
    senderPhoneNumberId: string | null = null
): Promise<SmsBulkSendResult> {
    const res = await axios.post<{ success: boolean; data: SmsBulkSendResult }>(
        '/box-truck-loads/sms/messages/v1',
        {
            driverIds,
            message,
            ...(thread ? { counterpart: thread.counterpart, line: thread.line } : {}),
            ...(senderPhoneNumberId ? { senderPhoneNumberId } : {}),
        },
        { baseURL: NON_V1_BASE }
    )
    return res.data.data
}

/**
 * Several US ZIPs to points, in one request.
 *
 * Through main-service rather than the browser's own geocoder: "US ZIP → point" belongs to
 * `@numeo_private/geo`, a frontend must not call an external host directly, and the public endpoint this
 * used to hit allows one request per second per client — a shared office IP pasting lists would earn
 * everyone behind it a 429.
 *
 * The response type is declared ONCE, in `@numeo/types`: two copies compile independently, so a field
 * renamed on the server would keep type-checking here and break the chips at runtime.
 */
export async function resolveZipCodes(zips: string[]): Promise<BoxTruckZipResolveResponse> {
    const res = await axios.post<{ success: boolean; data: BoxTruckZipResolveResponse }>('/box-truck-loads/zips/resolve/v1', { zips }, { baseURL: NON_V1_BASE })
    return res.data.data
}

export async function fetchBroadcastProgress(): Promise<BoxTruckBroadcastProgress[]> {
    const res = await axios.get<{ success: boolean; data: { items: BoxTruckBroadcastProgress[] } }>('/box-truck-loads/sms/broadcasts/v1', {
        baseURL: NON_V1_BASE,
    })
    return res.data?.data?.items ?? []
}

/**
 * Queue one message for more drivers than a send can carry.
 *
 * Answers as soon as it is written down, not when it is delivered: the server drains it a slice at a
 * time on the mailing tick, which is what makes it survive a closed tab and keeps a fleet-sized
 * blast from leaving one number all at once.
 */
export async function queueSmsBroadcast(
    driverIds: string[],
    message: string,
    /** The Quo line to send from. Null (the default) is whichever line the sender's own is. */
    senderPhoneNumberId: string | null = null
): Promise<{ id: string; total: number }> {
    const res = await axios.post<{ success: boolean; data: { id: string; total: number } }>(
        '/box-truck-loads/sms/broadcasts/v1',
        { driverIds, message, senderPhoneNumberId },
        { baseURL: NON_V1_BASE }
    )
    return res.data.data
}
